/**
 * Sign-in failure codes, and the copy the user actually reads.
 *
 * NextAuth has exactly one outcome for a `signIn` callback that returns `false`:
 * `/auth/error?error=AccessDenied`, captioned "You do not have permission to sign in."
 * That sentence is wrong for almost every real failure — a database fault, a provider
 * that withheld an email, and an actually-suspended account all rendered identically, and
 * it told users to stop trying when retrying was the right move.
 *
 * The `signIn` callback may instead return a URL string, which NextAuth redirects to
 * verbatim (next-auth 4.24 core/routes/callback.js). Handlers report a REASON, the
 * callback turns it into one of these codes, and /auth/error says something true.
 */
export type SignInErrorCode = 'account_suspended' | 'no_provider_email' | 'signin_failed' | 'too_many_codes';

/**
 * A sign-in rejected because of what the user typed, not because anything broke.
 *
 * The credentials providers reject bad input by throwing — that is the only way to get
 * a message back to NextAuth — and their `catch` blocks log whatever comes past at the
 * default `error` severity. So a mistyped OTP and a dead database landed on the same
 * shelf of the production error table, and on 2026-08-28 wrong codes and wrong
 * passwords were eight of its rows.
 *
 * Throwing this instead changes nothing the user sees: `message` is what NextAuth puts
 * on the wire either way. It only tells the `catch` block which of the two it caught.
 *
 * `name` is assigned explicitly because the class binding is minified in the Vercel
 * build — that is how `VendooRpcError` came to be filed as `J` — and `errorTypeName()`
 * reads `name` first for exactly this reason.
 */
export class AuthInputError extends Error {
  /**
   * `info` for a plain typo, which is a person doing nothing wrong on our side.
   * `warning` (the default) for everything that may be ours: an expired or
   * already-used code, a code sent elsewhere, a text that never arrived.
   */
  readonly severity: 'info' | 'warning';

  constructor(message: string, severity: 'info' | 'warning' = 'warning') {
    super(message);
    this.name = 'AuthInputError';
    this.severity = severity;
  }
}

/**
 * What the code step hears when there is no live code to check a typed one against:
 * it expired, it was already used, or none was sent to that email or phone. The
 * three share one message on purpose -- telling them apart would let anyone learn
 * whether a live code exists for someone else's email or phone. A mistyped code
 * against a live one keeps its own 'Invalid verification code'.
 */
export const CODE_EXPIRED_ERROR = 'Verification code expired';

/**
 * Said once a credential's guess budget is spent. A new code does not reset it,
 * so until it passes even the right code is refused; saying "invalid" then sent
 * people round asking for code after code (2026-11-02).
 */
export const TOO_MANY_CODES_ERROR = 'Too many wrong codes. Wait 15 minutes, then try the newest code.';
export const TOO_MANY_CODES_ERROR_CODE = 'TOO_MANY_ATTEMPTS';

/** verify-email: the email belongs to another account, and a sign-in code for it came back. */
export const EMAIL_HAS_ACCOUNT = 'EMAIL_HAS_ACCOUNT';

/** The redeem reasons that all read as {@link CODE_EXPIRED_ERROR}. */
export const isSpentCodeReason = (reason: string | undefined): boolean =>
  reason === 'expired' || reason === 'no_code' || reason === 'already_used';

/**
 * Severity for an auth failure: a typo is info, any other user-side refusal a
 * warning, ours an error. Read by name, not instanceof: the class binding is
 * minified in the build.
 */
export const authErrorSeverity = (error: unknown): 'info' | 'warning' | 'error' => {
  if (!(error instanceof Error) || error.name !== 'AuthInputError') return 'error';
  return (error as Partial<AuthInputError>).severity === 'info' ? 'info' : 'warning';
};

export interface SignInFailure {
  success: false;
  reason: SignInErrorCode;
  /**
   * Always absent — declared so callers can read `result.profile` without narrowing the
   * success/failure union first, which is how every existing call site is written.
   */
  profile?: undefined;
}

export const signInFailure = (reason: SignInErrorCode): SignInFailure => ({ success: false, reason });

/**
 * What an unproven OAuth login matched when the email gate stopped it, for the
 * error row only. `row_with_other_id` is the one to watch: the row already holds
 * a different id for this provider (Facebook ids are app-scoped, so a link made
 * by the legacy site never matches), and until that id is replaced every login
 * costs another code — the loop fixed in f132f6d5b.
 */
export type OAuthEmailGateMatch = 'new_account' | 'row_without_id' | 'row_with_other_id';

/**
 * What an OAuth handler returns when the login matched (or would create) an
 * account only through an email the provider did not prove. The sign-in is not
 * refused — the caller mails a code and redirects to the verify page — so this
 * is neither success nor a `SignInFailure`; it carries exactly what the
 * challenge needs to adopt the provider id once the inbox is proven.
 *
 * Flat and fully optional-typed (rather than a discriminated union) because the
 * project builds with `strict: false`, under which `if (result.needsEmailVerification)`
 * does not narrow — callers read the fields directly.
 */
export interface OAuthNeedsVerification {
  success: false;
  needsEmailVerification: true;
  provider: string;
  providerAccountId: string;
  email: string;
  name?: string | null;
  /** The existing USERS row to link to, or null when the login would create one. */
  userId: number | null;
  /** What the login matched, for the error row; see OAuthEmailGateMatch. */
  gateMatch?: OAuthEmailGateMatch;
  profile?: undefined;
  reason?: undefined;
}

export const oauthNeedsVerification = (
  provider: string,
  providerAccountId: string,
  email: string,
  name: string | null | undefined,
  userId: number | null,
  gateMatch?: OAuthEmailGateMatch
): OAuthNeedsVerification => ({
  success: false,
  needsEmailVerification: true,
  provider,
  providerAccountId,
  email,
  name: name ?? null,
  userId,
  gateMatch,
});

/** Where a failed OAuth sign-in lands. Relative, so it works on every deployed host. */
export const signInErrorUrl = (reason: SignInErrorCode | undefined): string =>
  `/auth/error?error=${reason ?? 'signin_failed'}`;

/**
 * The prefix a native-oauth sign-in puts on its error when the login needs the
 * email-code step. A credentials provider can only answer with an error string, so
 * the verify page's path rides in it and the app navigates there instead of showing
 * the string as a toast — the native equivalent of the web signIn callback's redirect.
 */
export const OAUTH_VERIFY_ERROR_PREFIX = 'oauth-verify:';

/** The verify-page path carried by a native-oauth error, or null for any other error. */
export const oauthVerifyPathFromError = (error: string | null | undefined): string | null => {
  if (!error?.startsWith(OAUTH_VERIFY_ERROR_PREFIX)) return null;
  const path = error.slice(OAUTH_VERIFY_ERROR_PREFIX.length);
  // Only ever our own verify page; anything else in the string is not followed.
  return path.startsWith('/auth/verify-oauth?') ? path : null;
};

interface ErrorCopy {
  title: string;
  message: string;
  /** False for terminal states, where a "Try Again" button is just a treadmill. */
  retryable: boolean;
}

export const SIGN_IN_ERROR_COPY: Record<string, ErrorCopy> = {
  account_suspended: {
    title: 'Account suspended',
    message:
      'This account has been suspended and cannot be used to sign in. If you think that is a mistake, contact support and we will take a look.',
    retryable: false,
  },
  no_provider_email: {
    title: "We didn't get your email address",
    message:
      'Your provider completed the sign-in but did not share an email address, and Sellwild needs one to identify your account. Check the permissions you granted, or sign in with your email instead.',
    retryable: true,
  },
  signin_failed: {
    title: "We couldn't finish signing you in",
    message:
      'Something went wrong on our end — this is not a problem with your account. Try again, and if it keeps happening let support know.',
    retryable: true,
  },
  too_many_codes: {
    title: 'We just sent you a code',
    message:
      'A sign-in code went to this address less than a minute ago. Use that one, or wait a minute and try again.',
    retryable: true,
  },

  // NextAuth's own codes, rewritten in plain language.
  Configuration: {
    title: 'Sign-in is misconfigured',
    message: 'Sellwild could not start the sign-in process. This one is on us — please contact support.',
    retryable: false,
  },
  Verification: {
    title: 'That link has expired',
    message: 'Sign-in links can only be used once, and they time out. Request a new one and it will work.',
    retryable: true,
  },
  OAuthSignin: {
    title: "We couldn't reach your provider",
    message: 'Sellwild could not start the handoff to Google, Apple or Facebook. Give it another try.',
    retryable: true,
  },
  OAuthCallback: {
    title: 'Your provider sent us back an error',
    message: 'The sign-in did not complete. Try again, or use a different sign-in method.',
    retryable: true,
  },
  OAuthCreateAccount: {
    title: "We couldn't create your account",
    message: 'Your provider signed you in, but we could not finish setting up your Sellwild account. Try again in a moment.',
    retryable: true,
  },
  EmailCreateAccount: {
    title: "We couldn't create your account",
    message: 'We could not finish setting up your Sellwild account with that email. Try again in a moment.',
    retryable: true,
  },
  Callback: {
    title: "We couldn't finish signing you in",
    message: 'The last step of sign-in failed. Try again, and if it keeps happening let support know.',
    retryable: true,
  },
  OAuthAccountNotLinked: {
    title: 'Use the provider you signed up with',
    message:
      'This email is already on a Sellwild account that was created with a different sign-in method. Use that method and we will link the two for you.',
    retryable: true,
  },
  // AccessDenied should no longer be reachable from our own handlers, but NextAuth can
  // still emit it for a provider-side denial.
  AccessDenied: {
    title: 'Sign-in was cancelled',
    message: 'Your provider did not approve the sign-in. Try again and accept the permission prompt.',
    retryable: true,
  },
};

export const DEFAULT_ERROR_COPY: ErrorCopy = {
  title: "We couldn't sign you in",
  message: 'Something unexpected happened. Try again, and if it keeps happening let support know.',
  retryable: true,
};

export const getSignInErrorCopy = (code: string | string[] | undefined): ErrorCopy => {
  const key = Array.isArray(code) ? code[0] : code;
  return (key && SIGN_IN_ERROR_COPY[key]) || DEFAULT_ERROR_COPY;
};
